import React from 'react';
import { C } from '../../theme.js';
import { Select, TextInput } from '../../components/Primitives.jsx';
import { ColSelect } from './ColSelect.jsx';

const CALC_OPS = [
  { value: '+', label: '+' },
  { value: '-', label: '−' },
  { value: '*', label: '×' },
  { value: '/', label: '÷' },
];
const OPERAND_MODES = [
  { value: 'static', label: 'Value' },
  { value: 'column', label: 'Column' },
];

function Operand({ spec, onChange, columns }) {
  const mode = spec?.mode || 'static';
  return (
    <div style={{ display: 'flex', gap: 4, flex: 1, minWidth: 130 }}>
      <Select value={mode} onChange={(v) => onChange({ mode: v, value: '' })} options={OPERAND_MODES} style={{ flex: '0 0 76px' }} />
      {mode === 'column' ? (
        <ColSelect value={spec?.value} onChange={(v) => onChange({ mode, value: v })} columns={columns} style={{ flex: 1, minWidth: 70 }} />
      ) : (
        <TextInput mono value={spec?.value ?? ''} onChange={(v) => onChange({ mode, value: v })} placeholder="number" style={{ flex: 1, minWidth: 50 }} />
      )}
    </div>
  );
}

// ── ResultValueField ─────────────────────────────────────────────────────
// What a Case When branch (or the else) writes: a literal, a copy of another
// column, or a numeric calculation (operand · operator · operand). `else`
// additionally offers "keep current value". Value shape:
//   { mode: 'literal'|'column'|'calc'|'keep', value, calc }
// Calc results are never rounded by the engine.
export function ResultValueField({ mode = 'literal', value, calc, onChange, columns, allowKeep = false, placeholder = 'result value' }) {
  const modes = [
    { value: 'literal', label: 'Value' },
    { value: 'column', label: 'Column' },
    { value: 'calc', label: 'Calculation' },
    ...(allowKeep ? [{ value: 'keep', label: 'Keep current' }] : []),
  ];
  const set = (patch) => onChange({ mode, value, calc, ...patch });
  const seedCalc = () => ({
    left: { mode: 'column', value: columns[0]?.name || '' }, op: '*', right: { mode: 'static', value: '1' },
  });
  const c = calc || seedCalc();
  return (
    <div style={{ display: 'flex', gap: 6, flex: 1, flexWrap: 'wrap', minWidth: 0 }}>
      <Select
        value={mode}
        onChange={(v) => set(v === 'calc' ? { mode: v, calc: calc || seedCalc() } : { mode: v, value: '' })}
        options={modes}
        style={{ flex: '0 0 116px' }}
      />
      {mode === 'literal' && (
        <TextInput mono value={value ?? ''} onChange={(v) => set({ value: v })} placeholder={placeholder} style={{ flex: 1, minWidth: 80 }} />
      )}
      {mode === 'column' && (
        <ColSelect value={value} onChange={(v) => set({ value: v })} columns={columns} style={{ flex: 1, minWidth: 90 }} />
      )}
      {mode === 'calc' && (
        <div style={{ display: 'flex', gap: 4, flex: '1 1 100%', flexWrap: 'wrap', alignItems: 'center' }}>
          <Operand spec={c.left} columns={columns} onChange={(left) => set({ calc: { ...c, left } })} />
          <Select value={c.op} onChange={(v) => set({ calc: { ...c, op: v } })} options={CALC_OPS} style={{ flex: '0 0 56px' }} />
          <Operand spec={c.right} columns={columns} onChange={(right) => set({ calc: { ...c, right } })} />
        </div>
      )}
      {mode === 'keep' && (
        <span style={{ color: C.fg5, fontSize: '.74rem', alignSelf: 'center' }}>Unmatched rows keep the target column&rsquo;s current value</span>
      )}
    </div>
  );
}
